// ChatPanel.jsx — AI assistant chat (v1, retrieval-only).
// Floating button (bottom-right) + right-side slide-over panel, desktop TM only.
// Talks to /api/chat (→ api/anthropic.js?action=chat → api/_chat.js).
//
// The panel stays mounted while the app is open, so conversation state
// survives open/close toggles. It is stateless server-side: each request
// sends the visible message history.
//
// Parent (App.jsx desktop / MobileApp.jsx mobile) passes:
//   context    — { tourId, tourName, artistName, dateRange } for grounding
//   onOpenShow — (showId) => void; navigates to the show (citation chips).
//                Omit it (mobile v1) and the chips simply don't render.
//   mobile     — full-screen sheet + launcher above the bottom nav + voice input
//
// Voice: browser SpeechRecognition (webkit-prefixed on iOS/Safari), feature-
// detected — the mic button only renders when the API exists. Dictation fills
// the input; the user still taps send (no auto-send — misheard bus-call
// questions shouldn't fire on their own).

import { useState, useRef, useEffect } from "react";
import { supabase } from "./supabase.js";

const SR = typeof window !== "undefined"
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

const SUGGESTIONS = [
  "What needs attention on this tour?",
  "Which shows aren't confirmed yet?",
  "What's the day schedule today?",
  "Where's the hotel tonight?",
];

const MOBILE_SUGGESTIONS = [
  "What time is bus call?",
  "Where's the hotel tonight?",
  "What's the schedule today?",
  "Who's on this tour?",
];

// Collect unique citation chips from a response trace, in date order
// (tool-call order can interleave, e.g. separate status-filtered queries).
function citesFromTrace(trace) {
  const seen = new Set();
  const out = [];
  for (const t of trace || []) {
    for (const c of t.cites || []) {
      if (!c.show_id || seen.has(c.show_id)) continue;
      seen.add(c.show_id);
      out.push(c);
    }
  }
  out.sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  return out.slice(0, 6);
}

const fmtChipDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return iso; }
};

// Lightweight message renderer — the model is instructed to reply in plain
// text, but this catches stray markdown so it never renders as literal **.
// Handles: **bold**, `- ` / `• ` / `1. ` list lines (rendered with a hanging
// indent), and blank-line paragraph spacing. No markdown library needed.
function renderInline(text, keyBase) {
  const parts = String(text).split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) =>
    i % 2 === 1 ? <strong key={`${keyBase}-${i}`} style={{ fontWeight: 700 }}>{p}</strong> : p
  );
}

function MessageBody({ content }) {
  const lines = String(content).split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].trim() === "") { i++; continue; }
    const listMatch = lines[i].match(/^\s*(?:[-•]|\d+[.)])\s+/);
    if (listMatch) {
      const items = [];
      while (i < lines.length) {
        const m = lines[i].match(/^\s*(?:[-•]|\d+[.)])\s+(.*)$/);
        if (!m) break;
        items.push(m[1]);
        i++;
      }
      blocks.push({ type: "list", items });
    } else {
      const para = [];
      while (i < lines.length && lines[i].trim() !== "" && !/^\s*(?:[-•]|\d+[.)])\s+/.test(lines[i])) {
        para.push(lines[i]);
        i++;
      }
      blocks.push({ type: "p", text: para.join("\n") });
    }
  }
  return (
    <>
      {blocks.map((b, bi) =>
        b.type === "list" ? (
          <div key={bi} style={{ margin: bi === 0 ? 0 : "6px 0 0", display: "flex", flexDirection: "column", gap: 3 }}>
            {b.items.map((it, ii) => (
              <div key={ii} style={{ display: "flex", gap: 7 }}>
                <span style={{ color: "var(--text-mut)", flexShrink: 0 }}>–</span>
                <span style={{ minWidth: 0 }}>{renderInline(it, `${bi}-${ii}`)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div key={bi} style={{ margin: bi === 0 ? 0 : "6px 0 0", whiteSpace: "pre-wrap" }}>
            {renderInline(b.text, bi)}
          </div>
        )
      )}
    </>
  );
}

const ACTION_LABELS = {
  set_show_status: "Change show status",
  send_broadcast: "Send broadcast",
  flag_advance_section: "Flag advance section",
  add_schedule_item: "Add schedule item",
  record_expense: "Record expense",
  accept_amendments: "Accept promoter answers",
  add_guest_list_entries: "Add guest list entries",
  add_contacts: "Add contacts",
  record_settlement: "Record settlement",
  set_show_promoter: "Set show promoter",
  update_venue: "Update venue record",
  update_deal: "Update deal terms",
  update_show_notes: "Update show notes",
  update_advance_fields: "Update advance fields",
  // Promoter portal actions
  update_fields: "Update advance answers",
  confirm_sections: "Confirm sections",
};

// Confirmation card for a proposed action. Nothing runs until Confirm —
// the commit goes through the same App functions the UI uses.
function ActionCard({ msg, onConfirm, onCancel }) {
  const a = msg.action;
  const busy = msg.state === "running";
  return (
    <div style={{
      maxWidth: "92%", borderRadius: 12, fontFamily: FF, overflow: "hidden",
      border: `1px solid ${msg.state === "error" ? "var(--danger)" : "color-mix(in srgb, var(--accent) 45%, transparent)"}`,
      background: "var(--panel-alt)",
      opacity: msg.state === "cancelled" ? 0.55 : 1,
    }}>
      <div style={{
        padding: "7px 12px", display: "flex", alignItems: "center", gap: 7,
        background: "color-mix(in srgb, var(--accent) 10%, transparent)",
        borderBottom: "1px solid color-mix(in srgb, var(--accent) 25%, transparent)",
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "var(--accent)" }}>
          {ACTION_LABELS[a.tool] || a.title || a.tool}
        </span>
      </div>
      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
        {(a.summary || []).map((line, i) => (
          <div key={i} style={{ fontSize: 12, lineHeight: 1.5, color: i === 0 ? "var(--text)" : "var(--text-mid)", fontWeight: i === 0 ? 600 : 400, overflowWrap: "break-word" }}>
            {line}
          </div>
        ))}
        {msg.state === "error" && (
          <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 2 }}>{msg.errorMsg || "Action failed — try again."}</div>
        )}
      </div>
      <div style={{ padding: "0 12px 10px", display: "flex", gap: 8, alignItems: "center" }}>
        {msg.state === "done" ? (
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--success)", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            Done
          </span>
        ) : msg.state === "cancelled" ? (
          <span style={{ fontSize: 11, color: "var(--text-mut)" }}>Cancelled</span>
        ) : (
          <>
            <button onClick={onCancel} disabled={busy}
              style={{ fontFamily: FF, fontSize: 12, padding: "5px 12px", borderRadius: 8, cursor: "pointer", background: "transparent", color: "var(--text-mut)", border: "1px solid var(--panel-border)" }}>
              Cancel
            </button>
            <button onClick={onConfirm} disabled={busy}
              style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "5px 14px", borderRadius: 8, cursor: busy ? "default" : "pointer", background: "var(--accent)", color: "var(--accent-fg, #0d0d0d)", border: "none", opacity: busy ? 0.6 : 1 }}>
              {busy ? "Working…" : "Confirm"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function ChatPanel({
  context = {}, onOpenShow, onExecuteAction, mobile = false,
  // Promoter portal mode — token auth instead of a Supabase session; the
  // parent supplies a fresh snapshot of the promoter-visible advance data.
  portalToken = null, getPortalData = null, suggestions = null,
}) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]); // { role, content, cites? }
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const recRef = useRef(null);
  const dictBaseRef = useRef(""); // input text present before dictation started

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, busy, open]);

  useEffect(() => {
    if (open && !mobile && inputRef.current) inputRef.current.focus(); // no auto-keyboard on mobile
  }, [open, mobile]);

  // Stop recognition if the panel closes or the component unmounts.
  useEffect(() => {
    if (!open && recRef.current) { try { recRef.current.stop(); } catch { /* noop */ } }
    return () => { if (recRef.current) { try { recRef.current.stop(); } catch { /* noop */ } } };
  }, [open]);

  const toggleVoice = () => {
    if (!SR) return;
    if (listening) {
      try { recRef.current?.stop(); } catch { /* noop */ }
      return;
    }
    try {
      const rec = new SR();
      rec.lang = navigator.language || "en-US";
      rec.interimResults = true;
      rec.continuous = false;
      dictBaseRef.current = input ? input.replace(/\s+$/, "") + " " : "";
      rec.onresult = (e) => {
        let transcript = "";
        for (let i = 0; i < e.results.length; i++) transcript += e.results[i][0].transcript;
        setInput(dictBaseRef.current + transcript);
      };
      rec.onend = () => { setListening(false); recRef.current = null; inputRef.current?.focus(); };
      rec.onerror = () => { setListening(false); recRef.current = null; };
      recRef.current = rec;
      setListening(true);
      rec.start();
    } catch {
      setListening(false);
    }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    const history = [...msgs, { role: "user", content: q }];
    setMsgs(history);
    setBusy(true);
    try {
      // Action cards + non-string content are excluded from history
      const histMsgs = history
        .filter((m) => (m.role === "user" || m.role === "assistant") && typeof m.content === "string")
        .map((m) => ({ role: m.role, content: m.content }));
      let body;
      const headers = { "Content-Type": "application/json" };
      if (portalToken) {
        body = {
          portalToken,
          portal: getPortalData ? getPortalData() : null,
          messages: histMsgs,
          context: { today: new Date().toLocaleDateString("en-CA") },
        };
      } else {
        const { data: sess } = await supabase.auth.getSession();
        const token = sess?.session?.access_token;
        if (!token) throw new Error("Not signed in");
        headers.Authorization = `Bearer ${token}`;
        body = {
          messages: histMsgs,
          context: {
            tourId: context.tourId || null,
            tourName: context.tourName || null,
            artistName: context.artistName || null,
            dateRange: context.dateRange || null,
            today: new Date().toLocaleDateString("en-CA"), // user-local ISO date
            allowActions: !!onExecuteAction, // server only offers write tools when the client can commit them
            screen: context.screen || null, // what the user is looking at right now
          },
        };
      }
      const res = await fetch("/api/chat", {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Request failed");
      setMsgs((prev) => {
        const next = [...prev];
        if (data.reply) next.push({ role: "assistant", content: data.reply, cites: citesFromTrace(data.trace) });
        if (data.pendingAction) next.push({ role: "action", action: data.pendingAction, state: "pending" });
        if (!data.reply && !data.pendingAction) next.push({ role: "assistant", content: "(no reply)" });
        return next;
      });
    } catch (e) {
      setMsgs((prev) => [...prev, { role: "assistant", content: `Something went wrong: ${e.message}`, error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const runAction = async (idx) => {
    const msg = msgs[idx];
    if (!msg || msg.role !== "action" || !onExecuteAction) return;
    setMsgs((prev) => prev.map((m, i) => (i === idx ? { ...m, state: "running" } : m)));
    let result;
    try {
      result = await onExecuteAction(msg.action);
    } catch (e) {
      result = { ok: false, message: e.message };
    }
    setMsgs((prev) => {
      const next = prev.map((m, i) =>
        i === idx ? { ...m, state: result?.ok ? "done" : "error", errorMsg: result?.message } : m
      );
      // Append the outcome as assistant text so follow-up questions have context
      if (result?.ok) next.push({ role: "assistant", content: `Done — ${result.message || "action completed."}` });
      return next;
    });
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* Floating toggle button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          title="Assistant"
          aria-label="Open assistant"
          style={{
            position: "fixed", zIndex: 8500,
            bottom: mobile ? "calc(68px + env(safe-area-inset-bottom, 0px))" : 22,
            right: mobile ? 16 : 22,
            width: mobile ? 44 : 48, height: mobile ? 44 : 48,
            borderRadius: 999, cursor: "pointer",
            background: "var(--accent)", color: "var(--accent-fg, #0d0d0d)",
            border: "none", display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 18px rgba(0,0,0,0.3)",
          }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {/* Panel — right slide-over on desktop, full-screen sheet on mobile */}
      {open && (
        <div style={{
          position: "fixed", top: 0, right: 0, bottom: 0, zIndex: mobile ? 9500 : 8500,
          left: mobile ? 0 : "auto",
          width: mobile ? "auto" : "min(390px, 92vw)",
          display: "flex", flexDirection: "column",
          paddingTop: mobile ? "env(safe-area-inset-top, 0px)" : 0,
          background: "var(--panel)", borderLeft: mobile ? "none" : "1px solid var(--panel-border)",
          boxShadow: mobile ? "none" : "-8px 0 32px rgba(0,0,0,0.25)", fontFamily: FF,
        }}>
          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", gap: 10, padding: "14px 16px",
            borderBottom: "1px solid var(--panel-border)", flexShrink: 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: "var(--text)" }}>ASSISTANT</div>
              {context.tourName && (
                <div style={{ fontSize: 11, color: "var(--text-mut)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {context.artistName ? `${context.artistName} · ` : ""}{context.tourName}
                </div>
              )}
            </div>
            {msgs.length > 0 && (
              <button onClick={() => setMsgs([])} title="Clear conversation"
                style={{ background: "none", border: "none", color: "var(--text-mut)", cursor: "pointer", padding: 4, display: "flex" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            )}
            <button onClick={() => setOpen(false)} aria-label="Close assistant"
              style={{ background: "none", border: "none", color: "var(--text-mut)", cursor: "pointer", padding: 4, display: "flex" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
            {msgs.length === 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, color: "var(--text-mut)", marginBottom: 12, lineHeight: 1.5 }}>
                  Ask about shows, advances, schedules, hotels, or logistics on this tour. Answers come straight from your tour data.
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {(suggestions || (mobile ? MOBILE_SUGGESTIONS : SUGGESTIONS)).map((s) => (
                    <button key={s} onClick={() => send(s)}
                      style={{
                        textAlign: "left", fontSize: 12, fontFamily: FF, cursor: "pointer",
                        padding: "8px 12px", borderRadius: 8, color: "var(--text-mid)",
                        background: "var(--panel-alt)", border: "1px solid var(--panel-border)",
                      }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {msgs.map((m, i) => m.role === "action" ? (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <ActionCard
                  msg={m}
                  onConfirm={() => runAction(i)}
                  onCancel={() => setMsgs((prev) => prev.map((x, xi) => (xi === i ? { ...x, state: "cancelled" } : x)))}
                />
              </div>
            ) : (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
                <div style={{
                  maxWidth: "88%", padding: "8px 12px", borderRadius: 12, fontSize: 12, lineHeight: 1.55,
                  overflowWrap: "break-word", color: "var(--text)",
                  background: m.role === "user" ? "color-mix(in srgb, var(--accent) 14%, var(--panel-alt))" : "var(--panel-alt)",
                  border: `1px solid ${m.error ? "var(--danger)" : "var(--panel-border)"}`,
                }}>
                  {m.role === "assistant" ? <MessageBody content={m.content} /> : m.content}
                </div>
                {onOpenShow && m.cites && m.cites.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
                    {m.cites.map((c) => (
                      <button key={c.show_id} onClick={() => onOpenShow && onOpenShow(c.show_id)}
                        title="Open this show"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer",
                          fontSize: 11, fontFamily: FF, padding: "3px 9px", borderRadius: 999,
                          color: "var(--accent)", background: "transparent",
                          border: "1px solid color-mix(in srgb, var(--accent) 45%, transparent)",
                        }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                        {fmtChipDate(c.date)}{c.venue ? ` · ${c.venue}` : ""}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {busy && (
              <div style={{
                alignSelf: "flex-start", padding: "12px 14px", borderRadius: 12,
                background: "var(--panel-alt)", border: "1px solid var(--panel-border)",
                display: "flex", gap: 4, alignItems: "center",
              }}>
                <style>{`
                  @keyframes chatDot { 0%, 60%, 100% { opacity: 0.25; transform: translateY(0); } 30% { opacity: 1; transform: translateY(-3px); } }
                  .chat-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--text-mut); animation: chatDot 1.2s infinite ease-in-out; }
                  .chat-dot:nth-child(3) { animation-delay: 0.15s; }
                  .chat-dot:nth-child(4) { animation-delay: 0.3s; }
                  @media (prefers-reduced-motion: reduce) { .chat-dot { animation: none; opacity: 0.6; } }
                `}</style>
                <span className="chat-dot" />
                <span className="chat-dot" />
                <span className="chat-dot" />
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{
            padding: "10px 12px", borderTop: "1px solid var(--panel-border)",
            paddingBottom: mobile ? "calc(10px + env(safe-area-inset-bottom, 0px))" : 10,
            display: "flex", gap: 8, alignItems: "flex-end", flexShrink: 0,
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={listening ? "Listening…" : "Ask about the tour…"}
              rows={1}
              style={{
                flex: 1, resize: "none", fontFamily: FF,
                fontSize: mobile ? 16 : 12, // ≥16px stops iOS zoom-on-focus
                lineHeight: 1.5,
                padding: "9px 12px", borderRadius: 8, color: "var(--text)",
                background: "var(--panel-alt)",
                border: `1px solid ${listening ? "var(--accent)" : "var(--field-border, var(--panel-border))"}`,
                outline: "none", maxHeight: 96, overflowY: "auto",
              }}
            />
            {SR && (
              <button
                onClick={toggleVoice}
                aria-label={listening ? "Stop dictation" : "Dictate"}
                title={listening ? "Stop dictation" : "Dictate"}
                style={{
                  width: 34, height: 34, borderRadius: 8, flexShrink: 0, cursor: "pointer",
                  border: `1px solid ${listening ? "var(--accent)" : "var(--panel-border)"}`,
                  background: listening ? "var(--accent)" : "var(--panel-alt)",
                  color: listening ? "var(--accent-fg, #0d0d0d)" : "var(--text-mut)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </button>
            )}
            <button
              onClick={() => send()}
              disabled={busy || !input.trim()}
              aria-label="Send"
              style={{
                width: 34, height: 34, borderRadius: 8, border: "none", flexShrink: 0,
                cursor: busy || !input.trim() ? "default" : "pointer",
                background: "var(--accent)", color: "var(--accent-fg, #0d0d0d)",
                opacity: busy || !input.trim() ? 0.4 : 1,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
